import mergedMaterialsData from '../../../data/merged-materials.json';
import {modelbankRequest} from '@/util/request';
import {getSupplierIdFromEmail} from '@/util/supplier-mapping';
import {store} from '@/store';
import {userAtom} from '@/store/user';
import {materialsAtom, materialsSearchParamsAtom} from '@/store/materials';
import {
    normalizedBrandToSupplierIdMapAtom,
    materialSuppliersAtom,
} from '@/store/materials/suppliers';
import type {Getter, Setter} from 'jotai';
import type {
    MaterialsResponse,
    Material,
    MaterialsState,
    MaterialsSearchParams,
} from '@/types/materials';

interface MaterialMeta {
    [key: string]: unknown;
}

interface MergedMaterial {
    id: number;
    meta: MaterialMeta;
}

const metaMap = new Map<number, MaterialMeta>(
    mergedMaterialsData.map((material: MergedMaterial) => [
        material.id,
        material.meta,
    ])
);

export async function getMaterials(get: Getter, set: Setter) {
    const searchParams = get(materialsSearchParamsAtom);
    const user = store.get(userAtom);
    const brandMap = get(normalizedBrandToSupplierIdMapAtom);
    const materialSuppliersState = get(materialSuppliersAtom);

    // Import retailer view store to get selected supplier ID
    const {selectedSupplierIdAtom} = await import('@/store/retailer-view');
    const selectedSupplierId = store.get(selectedSupplierIdAtom);

    let supplierIdToUse: string | undefined = undefined;
    let determinedSupplierIdForAtomUpdate: number | undefined = undefined;

    // Priority: 1) Explicit search param, 2) Selected retailer, 3) Email mapping, 4) Branding name
    if (searchParams.supplier_id) {
        supplierIdToUse = searchParams.supplier_id.toString();
    } else if (selectedSupplierId) {
        // Use selected retailer's supplier ID if in retailer view
        supplierIdToUse = selectedSupplierId.toString();
        determinedSupplierIdForAtomUpdate = selectedSupplierId;
    } else {
        if (user?.email) {
            const supplierIdFromEmail = getSupplierIdFromEmail(user.email);

            if (supplierIdFromEmail) {
                supplierIdToUse = supplierIdFromEmail.toString();
                determinedSupplierIdForAtomUpdate = parseInt(
                    supplierIdToUse,
                    10
                );
            }
        }
        if (!supplierIdToUse && user?.branding?.name) {
            const brandName = user.branding.name;

            if (!materialSuppliersState.initialized) {
                return set(materialsAtom, (prev: MaterialsState) => ({
                    ...prev,
                    loading: true,
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string should use fallback
                    error: prev.error || 'Waiting for supplier data...',
                    initialized: false,
                }));
            }

            const brandMapIsPopulated = Object.keys(brandMap).length > 0;

            if (brandMapIsPopulated) {
                const mappedSupplierId = brandMap[brandName];

                if (mappedSupplierId && typeof mappedSupplierId === 'string') {
                    supplierIdToUse = mappedSupplierId;
                    determinedSupplierIdForAtomUpdate = parseInt(
                        supplierIdToUse,
                        10
                    );
                }
            }
        }

        if (determinedSupplierIdForAtomUpdate) {
            set(materialsSearchParamsAtom, (prev: MaterialsSearchParams) => ({
                ...prev,
                supplier_id: determinedSupplierIdForAtomUpdate,
            }));
        }
    }

    if (!supplierIdToUse) {
        const errorMsg =
            '[getMaterials] Cannot determine supplier context. Please select a supplier or ensure user email/brand is correctly mapped.';

        return set(materialsAtom, (prev: MaterialsState) => ({
            ...prev,
            items: [],
            loading: false,
            error: errorMsg,
            initialized: true,
            pagination: {currentPage: 1, totalPages: 0, totalCount: 0},
        }));
    }

    set(materialsAtom, (prev: MaterialsState) => ({
        ...prev,
        loading: true,
        error: null,
    }));

    try {
        const queryParams = new URLSearchParams({
            page: searchParams.page.toString(),
            per_page: (searchParams.per_page ?? 48).toString(),
        });

        if (searchParams.kind) {
            queryParams.append('kind', searchParams.kind);
        }

        if (searchParams.properties) {
            queryParams.append('property_values', searchParams.properties);
        }

        if (supplierIdToUse) {
            queryParams.append('supplier_id', supplierIdToUse);
        }

        if (searchParams.q) {
            queryParams.append('q', searchParams.q);
        }

        if (searchParams.sort_by) {
            queryParams.append('sort_by', searchParams.sort_by);
            queryParams.append('sort_order', searchParams.sort_order ?? 'asc');
        }

        // Use modelbankRequest which handles token properly
        const endpoint = `/manage/product_materials?${queryParams.toString()}`;
        const response = await modelbankRequest<MaterialsResponse>(endpoint);

        const enrichedMaterials = response.product_materials.map(
            (material: Material) => {
                const metaData = metaMap.get(material.id);

                if (metaData) {
                    return {...material, meta: metaData} as Material & {
                        meta: MaterialMeta;
                    };
                }

                return material;
            }
        );

        set(materialsAtom, (prev: MaterialsState) => ({
            ...prev,
            items:
                searchParams.page === 1
                    ? enrichedMaterials
                    : [...prev.items, ...enrichedMaterials],
            pagination: {
                currentPage: response.pagination.current_page,
                totalPages: response.pagination.total_pages,
                totalCount: response.pagination.total_count,
            },
            loading: false,
            initialized: true,
        }));
    } catch (error) {
        set(materialsAtom, (prev: MaterialsState) => ({
            ...prev,
            loading: false,
            error:
                error instanceof Error
                    ? error.message
                    : 'Failed to fetch materials',
        }));
    }
}

export async function getMaterialById(id: number): Promise<Material> {
    // Use modelbankRequest which handles token properly
    const endpoint = `/manage/product_materials/${id}`;
    const material = await modelbankRequest<Material>(endpoint);

    if (!material) throw new Error(`Material with ID ${id} not found.`);

    const metaData = metaMap.get(material.id);

    if (metaData) {
        return {...material, meta: metaData} as Material & {meta: MaterialMeta};
    }

    return material;
}

export async function getMaterialsByIds(
    ids: number[]
): Promise<Map<number, Material>> {
    const endpoint = `/manage/product_materials/ids`;
    const materials = await modelbankRequest<Material[]>(endpoint, 'POST', {
        ids,
    });

    const result = new Map<number, Material>();
    for (const material of materials) {
        const metaData = metaMap.get(material.id);
        result.set(
            material.id,
            metaData
                ? ({...material, meta: metaData} as Material & {
                      meta: MaterialMeta;
                  })
                : material
        );
    }
    return result;
}

export async function searchMaterialsBySku(
    sku: string
): Promise<Material | null> {
    const endpoint = `/manage/product_materials?q=${encodeURIComponent(sku)}&per_page=50`;
    const response = await modelbankRequest<MaterialsResponse>(endpoint);

    const exact = response.product_materials.find((m) => m.sku === sku);
    return exact ?? null;
}

export function resolveSupplierIdSync(): string | undefined {
    const user = store.get(userAtom);

    // 1) Explicit search param
    const searchParams = store.get(materialsSearchParamsAtom);
    if (searchParams.supplier_id) {
        return searchParams.supplier_id.toString();
    }

    // 2) Email mapping
    if (user?.email) {
        const fromEmail = getSupplierIdFromEmail(user.email);
        if (fromEmail) return fromEmail.toString();
    }

    // 3) Branding name → supplier map
    if (user?.branding?.name) {
        const brandMap = store.get(normalizedBrandToSupplierIdMapAtom);
        const mapped = brandMap[user.branding.name];
        if (mapped && typeof mapped === 'string') return mapped;
    }

    // 4) user.supplier_id as last resort
    if (user?.supplier_id) return String(user.supplier_id);

    return undefined;
}

export async function searchMaterialsBySkus(
    skus: string[]
): Promise<Map<string, Material>> {
    const supplierId = resolveSupplierIdSync();

    const results = await Promise.allSettled(
        skus.map(async (sku) => {
            const params = new URLSearchParams({
                q: sku,
                per_page: '50',
            });
            if (supplierId) {
                params.set('supplier_id', supplierId);
            }
            const endpoint = `/manage/product_materials?${params}`;
            const response =
                await modelbankRequest<MaterialsResponse>(endpoint);
            const exact = response.product_materials.find((m) => m.sku === sku);
            return {sku, material: exact ?? null};
        })
    );

    const map = new Map<string, Material>();
    for (const entry of results) {
        if (entry.status === 'fulfilled' && entry.value.material) {
            map.set(entry.value.sku, entry.value.material);
        }
    }
    return map;
}
